import { useState, useEffect, useCallback } from "react";

import { useWithingsAuth } from "@/domains/health/withings/hooks/useWithingsAuth";
import { useYazioSync } from "@/domains/health/yazio/hooks/use-yazio-sync";
import { debugHealth } from "@/domains/logging/utils/debugLog";

export interface SmartSyncState {
  error: null | string;
  isLoading: boolean;
  isWithingsAuthenticated: boolean;
  isWithingsSynced: boolean;
  isYazioSynced: boolean;
  lastWithingsSync: Date | null;
  lastYazioSync: Date | null;
}

export interface SmartSyncActions {
  checkSyncStatus: () => Promise<void>;
  syncAll: () => Promise<void>;
  syncWithings: () => Promise<void>;
  syncYazio: () => Promise<void>;
}

export type UseSmartSyncReturn = SmartSyncState & SmartSyncActions;

/**
 * SmartSync Hook for Dashboard
 *
 * Automatically manages data synchronization for Withings and Yazio
 * when the dashboard is loaded, ensuring data is always up-to-date.
 *
 * @returns {UseSmartSyncReturn} Object with sync state and actions
 *
 */
export const useSmartSync = (): UseSmartSyncReturn => {
  const [state, setState] = useState<SmartSyncState>({
    error: null,
    isLoading: false,
    isWithingsAuthenticated: false,
    isWithingsSynced: false,
    isYazioSynced: false,
    lastWithingsSync: null,
    lastYazioSync: null,
  });

  // Get Withings auth status
  const { isAuthenticated: isWithingsAuthenticated, syncData: withingsSyncData } =
    useWithingsAuth();

  // Get Yazio sync functions
  const { lastSyncDate, syncTodayData } = useYazioSync();

  /**
   * Sync Withings data
   */
  const syncWithings = useCallback(async () => {
    if (!isWithingsAuthenticated) {
      debugHealth("info", "dashboard", "Withings not authenticated, skipping sync", {
        file: "useSmartSync.ts",
        function: "syncWithings",
      });
      return;
    }

    try {
      setState((previous) => ({ ...previous, error: null, isLoading: true }));

      debugHealth("info", "dashboard", "Starting Withings sync", {
        file: "useSmartSync.ts",
        function: "syncWithings",
      });

      await withingsSyncData();

      setState((previous) => ({
        ...previous,
        isLoading: false,
        isWithingsSynced: true,
        lastWithingsSync: new Date(),
      }));

      debugHealth("info", "dashboard", "Withings sync completed", {
        file: "useSmartSync.ts",
        function: "syncWithings",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sync Withings data";
      setState((previous) => ({
        ...previous,
        error: errorMessage,
        isLoading: false,
      }));

      debugHealth("error", "dashboard", "Withings sync failed", {
        error: errorMessage,
        file: "useSmartSync.ts",
        function: "syncWithings",
      });
    }
  }, [isWithingsAuthenticated, withingsSyncData]);

  /**
   * Sync Yazio data
   */
  const syncYazio = useCallback(async () => {
    try {
      setState((previous) => ({ ...previous, error: null, isLoading: true }));

      debugHealth("info", "dashboard", "Starting Yazio sync", {
        file: "useSmartSync.ts",
        function: "syncYazio",
      });

      const result = await syncTodayData();

      if (result.success) {
        setState((previous) => ({
          ...previous,
          isLoading: false,
          isYazioSynced: true,
          lastYazioSync: new Date(),
        }));

        debugHealth("info", "dashboard", "Yazio sync completed", {
          dataSynced: result.data_synced,
          file: "useSmartSync.ts",
          function: "syncYazio",
        });
      } else {
        throw new Error(result.message || "Yazio sync failed");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sync Yazio data";
      setState((previous) => ({
        ...previous,
        error: errorMessage,
        isLoading: false,
      }));

      debugHealth("error", "dashboard", "Yazio sync failed", {
        error: errorMessage,
        file: "useSmartSync.ts",
        function: "syncYazio",
      });
    }
  }, [syncTodayData]);

  /**
   * Sync all services
   */
  const syncAll = useCallback(async () => {
    debugHealth("info", "dashboard", "Starting full sync for all services", {
      file: "useSmartSync.ts",
      function: "syncAll",
    });

    setState((previous) => ({ ...previous, error: null, isLoading: true }));

    try {
      // Sync both services in parallel
      await Promise.allSettled([syncWithings(), syncYazio()]);

      setState((previous) => ({ ...previous, isLoading: false }));

      debugHealth("info", "dashboard", "Full sync completed", {
        file: "useSmartSync.ts",
        function: "syncAll",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sync all services";
      setState((previous) => ({
        ...previous,
        error: errorMessage,
        isLoading: false,
      }));

      debugHealth("error", "dashboard", "Full sync failed", {
        error: errorMessage,
        file: "useSmartSync.ts",
        function: "syncAll",
      });
    }
  }, [syncWithings, syncYazio]);

  /**
   * Check current sync status
   */
  const checkSyncStatus = useCallback(async () => {
    setState((previous) => ({
      ...previous,
      isWithingsAuthenticated,
      lastYazioSync: lastSyncDate ?? null,
    }));
  }, [isWithingsAuthenticated, lastSyncDate]);

  // Only check sync status on mount, no auto-sync
  useEffect(() => {
    checkSyncStatus();
  }, [checkSyncStatus]);

  return {
    ...state,
    checkSyncStatus,
    syncAll,
    syncWithings,
    syncYazio,
  };
};
